import { useRef, useState } from 'react'
import {
  Box, Checkbox, Divider, ListItemText, Menu, MenuItem, Tooltip, useTheme
} from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { C33Column } from './columns'
import { TablePrefs } from './use-table-prefs'
import { c100Tokens } from './c100-tokens'

const MONO = "'IBM Plex Mono', monospace"

// Column visibility plus a way back to the shipped layout. Listed in the user's
// current order so the menu matches what they see in the table.
export default function ColumnPicker({
  columns,
  prefs,
  onToggle,
  onReset,
  isDirty
}: {
  columns: C33Column[]
  prefs: TablePrefs
  onToggle: (id: string) => void
  onReset: () => void
  isDirty: boolean
}) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const byId = new Map(columns.map(c => [c.id, c]))
  const ordered = prefs.order.map(id => byId.get(id)).filter((c): c is C33Column => !!c)
  const shownCount = ordered.filter(c => !prefs.hidden.includes(c.id)).length

  return (
    <>
      <Tooltip title={`Columns — ${shownCount} of ${ordered.length} shown`} arrow>
        <Box
          component="button"
          aria-label="Choose columns"
          ref={btnRef}
          onClick={() => setOpen(o => !o)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            cursor: 'pointer',
            color: isDirty ? t.accent : t.muted,
            bgcolor: 'transparent',
            border: 'none',
            borderRadius: '6px',
            p: 0,
            position: 'relative',
            transition: 'color .15s, background-color .15s',
            '&:hover': { color: t.text, bgcolor: t.faintAccentBg }
          }}
        >
          <ViewColumnIcon sx={{ fontSize: 17 }} />
          {/* a dot, not a number: at this size a count is unreadable, and all
              that matters is whether the layout differs from the shipped one */}
          {isDirty && (
            <Box
              sx={{
                position: 'absolute',
                top: 3,
                right: 3,
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: t.accent
              }}
            />
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={btnRef.current}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: t.card,
              border: `1px solid ${t.hairline}`,
              borderRadius: 2,
              maxHeight: 420
            }
          }
        }}
      >
        {ordered.map(col => {
          const visible = !prefs.hidden.includes(col.id)
          return (
            <MenuItem
              key={col.id}
              dense
              disabled={col.locked}
              onClick={() => onToggle(col.id)}
              sx={{ py: 0.25, pr: 2.5 }}
            >
              <Checkbox
                size="small"
                checked={visible || !!col.locked}
                disabled={col.locked}
                sx={{ p: 0.5, mr: 1, color: t.muted, '&.Mui-checked': { color: t.accent } }}
              />
              <ListItemText
                primary={col.label}
                secondary={col.locked ? 'Always shown' : undefined}
                primaryTypographyProps={{ fontSize: 12.5, color: t.text }}
                secondaryTypographyProps={{ fontSize: 10, fontFamily: MONO, color: t.muted }}
              />
            </MenuItem>
          )
        })}

        <Divider sx={{ borderColor: t.hairline, my: 0.5 }} />
        <MenuItem
          dense
          disabled={!isDirty}
          onClick={() => {
            onReset()
            setOpen(false)
          }}
          sx={{
            fontFamily: MONO,
            fontSize: 11,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: t.accent,
            py: 0.75
          }}
        >
          Reset layout
        </MenuItem>
      </Menu>
    </>
  )
}
